import type { Booking, BookingStatus, PayStatus } from '../../../mocks/bookings';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

export const statusConfig: Record<BookingStatus, { bg: string; text: string; dot: string }> = {
  Confirmed:  { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Pending:    { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  Cancelled:  { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500' },
  Completed:  { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-400' },
  'On Hold':  { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500' },
};

export const payStatusConfig: Record<PayStatus, { bg: string; text: string }> = {
  Paid:     { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  Partial:  { bg: 'bg-amber-50',   text: 'text-amber-700' },
  Unpaid:   { bg: 'bg-red-50',     text: 'text-red-700' },
  Refunded: { bg: 'bg-gray-100',   text: 'text-gray-500' },
};

interface BookingTableProps {
  bookings: Booking[];
  onSelect: (b: Booking) => void;
}

export default function BookingTable({ bookings, onSelect }: BookingTableProps) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-100">
      <table className="w-full">
        <thead className="bg-gray-50/80">
          <tr>
            {['Booking', 'Customer', 'Service / Destination', 'Travel Dates', 'Pax', 'Status', 'Amount', 'Payment', 'Agent', ''].map((h) => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-50">
          {bookings.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-16 text-center text-sm text-gray-400">
                No bookings found.
              </td>
            </tr>
          )}
          {bookings.map((bk) => {
            const sc = statusConfig[bk.status];
            const pc = payStatusConfig[bk.payStatus];
            const balance = bk.totalAmount - bk.paidAmount;
            return (
              <tr
                key={bk.id}
                onClick={() => onSelect(bk)}
                className="hover:bg-gray-50/60 cursor-pointer transition-colors"
              >
                {/* Booking ID */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${brand}15` }}
                    >
                      <i className={`${bk.serviceIcon} text-sm`} style={{ color: brand }} />
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: brand }}>{bk.id}</p>
                      <p className="text-xs text-gray-400">{bk.serviceType}</p>
                    </div>
                  </div>
                </td>

                {/* Customer */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: brand }}
                    >
                      {bk.customerAvatar}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">{bk.customer}</p>
                      <p className="text-xs text-gray-400 whitespace-nowrap">{bk.customerPhone}</p>
                    </div>
                  </div>
                </td>

                {/* Service */}
                <td className="px-4 py-3.5 max-w-[200px]">
                  <p className="text-sm text-gray-700 font-medium truncate">{bk.service}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <i className="ri-map-pin-line text-xs text-gray-400" />
                    <p className="text-xs text-gray-400">{bk.destination}</p>
                  </div>
                </td>

                {/* Dates */}
                <td className="px-4 py-3.5">
                  <p className="text-sm text-gray-700 whitespace-nowrap">{bk.travelDate}</p>
                  <p className="text-xs text-gray-400 whitespace-nowrap">{bk.returnDate} · {bk.nights}N</p>
                </td>

                {/* Pax */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-1 text-gray-600">
                    <i className="ri-user-line text-sm" />
                    <span className="text-sm font-semibold">{bk.pax}</span>
                  </div>
                </td>

                {/* Status */}
                <td className="px-4 py-3.5">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                    {bk.status}
                  </span>
                </td>

                {/* Amount */}
                <td className="px-4 py-3.5">
                  <p className="text-sm font-bold text-gray-800 whitespace-nowrap">
                    {bk.currency} {bk.totalAmount.toLocaleString()}
                  </p>
                  {balance > 0 && bk.payStatus !== 'Refunded' && (
                    <p className="text-xs text-red-500 whitespace-nowrap">
                      -{bk.currency} {balance.toLocaleString()} due
                    </p>
                  )}
                </td>

                {/* Pay Status */}
                <td className="px-4 py-3.5">
                  <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${pc.bg} ${pc.text}`}>
                    {bk.payStatus}
                  </span>
                </td>

                {/* Agent */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: brand }}
                    >
                      {bk.agentAvatar}
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{bk.agent}</span>
                  </div>
                </td>

                {/* Actions */}
                <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); onSelect(bk); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                    >
                      <i className="ri-eye-line text-sm" />
                    </button>
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
                      <i className="ri-edit-line text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
